/** Relation / sex helpers for labels and chip keys of saved charts. */

export type RelationKey = 'self' | 'son' | 'daughter' | 'mother' | 'father' | 'partner' | 'friend' | 'colleague' | 'other';
export type SexKey = 'male' | 'female' | 'neutral';

const RELATION_KEYS: ReadonlySet<string> = new Set([
  'self', 'son', 'daughter', 'mother', 'father', 'partner', 'friend', 'colleague', 'other',
]);

/** 'SELF' / isSelf → 'self'; null → 'other'; otherwise lowercase. */
export function relationKey(relation: string | null | undefined, isSelf?: boolean): RelationKey {
  if (isSelf) return 'self';
  if (!relation) return 'other';
  const key = relation.toLowerCase();
  return RELATION_KEYS.has(key) ? (key as RelationKey) : 'other';
}

/** Explicit sex wins; else SON/FATHER → male, DAUGHTER/MOTHER → female; else neutral. Same rule as the server's effectiveSex. */
export function sexKey(sex: string | null | undefined, relation: string | null | undefined): SexKey {
  if (sex === 'MALE') return 'male';
  if (sex === 'FEMALE') return 'female';
  if (relation === 'SON' || relation === 'FATHER') return 'male';
  if (relation === 'DAUGHTER' || relation === 'MOTHER') return 'female';
  return 'neutral';
}

/**
 * i18n key (namespace `me`) of the label shown next to a saved person's name in cards and headers.
 * OTHER / missing relation reads «близкий человек» here, not the form option «Другое».
 * FRIEND of a woman (effective sex, see `sexKey`) reads «подруга» in ru / uk. The form's option chips keep
 * the plain `charts.relation.*` keys.
 */
export function relationDisplayKey(
  relation: string | null | undefined,
  isSelf?: boolean,
  sex?: string | null,
): string {
  const key = relationKey(relation, isSelf);
  if (key === 'other') return 'charts.relationDisplay.other';
  if (key === 'friend' && sexKey(sex, relation) === 'female') return 'charts.relationDisplay.friendFemale';
  return `charts.relation.${key}`;
}
